// src/components/layout/AppLayout.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  canInstall, onInstallAvailabilityChange, promptInstall, applyUpdate,
} from '../../lib/pwa';

const NAV = [
  { id: 'workboard',  label: 'Workboard',    icon: '🌸', badge: 'action' },
  { id: 'dashboard',  label: 'Dashboard',    icon: '📊' },
  { id: 'orders',     label: 'Orders',        icon: '📋' },
  { id: 'new-order',  label: 'New Order',     icon: '➕' },
  { id: 'customers',  label: 'Customers',     icon: '👥' },
  { id: 'products',   label: 'Products',      icon: '🌹' },
  { id: 'employees',  label: 'Team',          icon: '👤' },
  { id: 'reports',    label: 'Reports',       icon: '📈' },
  { id: 'settings',   label: 'Settings',      icon: '⚙️' },
];

const PAGE_TITLES = {
  workboard: 'Workboard',
  dashboard: 'Dashboard',
  orders:    'Orders',
  'new-order': 'New Order',
  customers: 'Customers',
  products:  'Products',
  employees: 'Team',
  reports:   'Reports',
  settings:  'Settings',
};

export function AppLayout({ page, setPage, children, actionCount = 0 }) {
  const { shop, profile, logout, isOwner } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [installable,  setInstallable]  = useState(canInstall());
  const [updateReady,  setUpdateReady]  = useState(false);

  // Chrome fires beforeinstallprompt asynchronously, so the button appears a
  // moment after load rather than being there on first paint.
  useEffect(() => onInstallAvailabilityChange(setInstallable), []);
  useEffect(() => {
    const onUpdate = () => setUpdateReady(true);
    window.addEventListener('agtpos:update-ready', onUpdate);
    return () => window.removeEventListener('agtpos:update-ready', onUpdate);
  }, []);

  const initials = profile?.displayName
    ? profile.displayName.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)
    : '?';

  const visibleNav = NAV.filter(n => {
    if (n.id === 'employees' && !isOwner) return false;
    if (n.id === 'settings'  && !isOwner) return false;
    return true;
  });

  return (
    <div className="app-shell">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-name">AGT<span>POS</span></div>
          <div className="shop-name">{shop?.name || 'Loading…'}</div>
        </div>

        <div className="sidebar-section">
          <div className="sidebar-label">Menu</div>
          {visibleNav.map(n => (
            <button
              key={n.id}
              className={`nav-link ${page === n.id ? 'active' : ''}`}
              onClick={() => setPage(n.id)}
            >
              <span className="nav-icon">{n.icon}</span>
              <span>{n.label}</span>
              {n.badge === 'action' && actionCount > 0 && (
                <span className="alert-badge red">{actionCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="sidebar-bottom">
          <button className="nav-link" onClick={logout}>
            <span className="nav-icon">🚪</span>
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="main-content">
        <header className="topbar">
          <div className="topbar-title">{PAGE_TITLES[page] || page}</div>
          <div className="topbar-right">
            {updateReady && (
              <button className="btn btn-gold btn-sm" onClick={applyUpdate}
                title="A newer version has been downloaded">
                ⬆ Update ready
              </button>
            )}
            {installable && (
              <button className="btn btn-secondary btn-sm" onClick={promptInstall}
                title="Install AGT POS on this computer">
                ⬇ Install app
              </button>
            )}
            <div className="user-chip" style={{cursor:'pointer'}} onClick={()=>setShowUserMenu(v=>!v)}>
              <div className="user-avatar">{initials}</div>
              <span>{profile?.displayName}</span>
              {isOwner && <span className="pill pill-green" style={{fontSize:10,padding:'2px 7px'}}>Owner</span>}
            </div>
            {showUserMenu && (
              <div style={{
                position:'absolute',top:58,right:20,
                background:'white',border:'1px solid var(--border)',
                borderRadius:'var(--radius)',boxShadow:'var(--shadow-md)',
                zIndex:100,minWidth:160,padding:'6px 0'
              }}>
                <button className="nav-link" style={{color:'var(--text)',padding:'9px 16px'}} onClick={()=>{ setPage('settings'); setShowUserMenu(false); }}>
                  Settings
                </button>
                <hr className="divider" style={{margin:'4px 0'}}/>
                <button className="nav-link" style={{color:'var(--red)',padding:'9px 16px'}} onClick={logout}>
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="page-body">
          {children}
        </div>
      </div>
    </div>
  );
}
